/**
 * OrgHierarchyPage — Organization Hierarchy Management
 *
 * Admin page to view, add, edit, delete, and move org nodes.
 * Uses OrgHierarchyTree component for the visual tree.
 *
 * Route: /admin/org-hierarchy
 *
 * Phase 0 — Multi-Site Foundation
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../../context/OrgContext';
import { useAlert } from '../../context/AlertContext';
import { useAuth } from '../../context/AuthContext';
import OrgHierarchyTree, { NodeFormModal, MoveNodeModal, DeleteNodeModal, StatutoryFormsModal } from '../../components/OrgHierarchyTree';
import TabNavigation from '../../components/TabNavigation';
import SetupWizard from './SetupWizard';

export default function OrgHierarchyPage({ user, onLogout, hideNavigation = false }) {
  const navigate = useNavigate();
  const { nodes, loading, error, createNode, updateNode, deleteNode, getNodeDeletionImpact, moveNode, refreshTree } = useOrg();
  const { showSuccess, showError } = useAlert();
  const { user: authUser } = useAuth();

  const isAdmin =
    user?.role === 'admin' || user?.role === 'Admin' ||
    authUser?.role === 'admin' || authUser?.role === 'Admin';

  // System roles for role mapping in NodeFormModal
  const [systemRoles, setSystemRoles] = useState([]);
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = localStorage.getItem('token');
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${backendUrl}/api/tenant-admin/roles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setSystemRoles(data || []);
        }
      } catch (err) {
        console.log('Failed to fetch system roles:', err);
      }
    };
    fetchRoles();
  }, []);

  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [editingNode, setEditingNode] = useState(null);   // node being edited
  const [parentNode, setParentNode] = useState(null);     // parent when adding child
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [highlightId, setHighlightId] = useState(null);

  // Move modal state
  const [movingNode, setMovingNode] = useState(null);
  const [moveError, setMoveError] = useState(null);
  const [moveSaving, setMoveSaving] = useState(false);

  // Statutory forms modal state (country/state nodes)
  const [statutoryNode, setStatutoryNode] = useState(null);

  // Delete modal state (type-to-confirm)
  const [deletingNode, setDeletingNode] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);

  const openAdd = (parent) => {
    setEditingNode(null);
    setParentNode(parent);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (node) => {
    setEditingNode(node);
    setParentNode(null);
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingNode(null);
    setParentNode(null);
    setFormError(null);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    setFormError(null);
    try {
      if (editingNode) {
        await updateNode(editingNode.id, payload);
        showSuccess(`"${payload.name}" updated.`);
        setHighlightId(editingNode.id);
      } else {
        const created = await createNode(payload);
        showSuccess(`"${payload.name}" created.`);
        setHighlightId(created?.id ?? null);
      }
      closeForm();
      setTimeout(() => setHighlightId(null), 2500);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // "Add Site" — find the best parent node for a new site (deepest valid intermediate or org)
  const openAddSite = () => {
    const validParentTypes = ['business_unit', 'country', 'other', 'region', 'organization'];
    let bestParent = null;
    for (const type of validParentTypes) {
      bestParent = nodes.find((n) => n.node_type === type);
      if (bestParent) break;
    }
    openAdd(bestParent || null);
  };

  // Open the type-to-confirm delete modal and load the data-impact counts.
  const openDelete = async (node) => {
    setDeletingNode(node);
    setDeleteError(null);
    setDeleteImpact(null);
    setImpactLoading(true);
    try {
      const impact = await getNodeDeletionImpact(node.id);
      setDeleteImpact(impact);
    } catch (err) {
      // Non-fatal: the admin can still delete; just flag that counts are unavailable.
      setDeleteError(`Could not load attached-record counts: ${err.message}`);
    } finally {
      setImpactLoading(false);
    }
  };

  const closeDelete = () => {
    setDeletingNode(null);
    setDeleteError(null);
    setDeleteImpact(null);
    setImpactLoading(false);
  };

  const confirmDelete = async () => {
    if (!deletingNode) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      // force=true soft-deletes the whole subtree if the node has children.
      await deleteNode(deletingNode.id, { force: true });
      showSuccess(`"${deletingNode.name}" deleted.`);
      closeDelete();
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const openMove = (node) => {
    setMovingNode(node);
    setMoveError(null);
  };

  const handleMove = async (newParentId) => {
    setMoveSaving(true);
    setMoveError(null);
    try {
      await moveNode(movingNode.id, newParentId);
      showSuccess(`"${movingNode.name}" moved successfully.`);
      setMovingNode(null);
      setHighlightId(movingNode.id);
      setTimeout(() => setHighlightId(null), 2500);
    } catch (err) {
      setMoveError(err.message);
    } finally {
      setMoveSaving(false);
    }
  };

  // Show Setup Wizard inline when no hierarchy exists yet
  const showWizard = !loading && isAdmin && nodes.length === 0;

  const content = showWizard ? (
    <div className={hideNavigation ? '' : 'max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
      <SetupWizard user={user} onLogout={onLogout} hideNavigation={true} onComplete={refreshTree} />
    </div>
  ) : (
    <>
      <div className={hideNavigation ? '' : 'max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Organization Hierarchy</h1>
            <p className="text-gray-500 mt-1 text-sm">
              Manage your multi-site org structure — use standard layers or define custom ones.
            </p>
          </div>
          <div className="flex gap-2">
            {isAdmin && nodes.length > 0 && (
              <button
                onClick={openAddSite}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Site
              </button>
            )}
            <button
              onClick={refreshTree}
              className="px-3 py-2 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50"
              title="Refresh"
            >
              ↻
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Tree panel */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
              <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Loading hierarchy…
            </div>
          ) : (
            <OrgHierarchyTree
              nodes={nodes}
              onAdd={isAdmin ? openAdd : undefined}
              onEdit={isAdmin ? openEdit : undefined}
              onDelete={isAdmin ? openDelete : undefined}
              onMove={isAdmin ? openMove : undefined}
              onStatutoryForms={isAdmin ? setStatutoryNode : undefined}
              readOnly={!isAdmin}
              highlightId={highlightId}
            />
          )}
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-3 text-xs text-gray-500">
          {[
            { label: 'Organization', color: 'bg-purple-100 text-purple-800' },
            { label: 'Region',       color: 'bg-blue-100 text-blue-800' },
            { label: 'Country',      color: 'bg-cyan-100 text-cyan-800' },
            { label: 'Business Unit',color: 'bg-indigo-100 text-indigo-800' },
            { label: 'Custom',       color: 'bg-amber-100 text-amber-800' },
            { label: 'Site',         color: 'bg-green-100 text-green-800' },
            { label: 'Sub-Site',     color: 'bg-teal-100 text-teal-800' },
            { label: 'Prod. Line',   color: 'bg-orange-100 text-orange-800' },
          ].map(({ label, color }) => (
            <span key={label} className={`px-2 py-0.5 rounded-full font-medium ${color}`}>{label}</span>
          ))}
        </div>
      </div>

      {/* Node Form Modal */}
      {showForm && (
        <NodeFormModal
          node={editingNode}
          parentNode={parentNode}
          onClose={closeForm}
          onSave={handleSave}
          saving={saving}
          error={formError}
          systemRoles={systemRoles}
          allNodes={nodes}
        />
      )}

      {movingNode && (
        <MoveNodeModal
          node={movingNode}
          allNodes={nodes}
          onClose={() => setMovingNode(null)}
          onSave={handleMove}
          saving={moveSaving}
          error={moveError}
        />
      )}

      {statutoryNode && (
        <StatutoryFormsModal
          node={statutoryNode}
          onClose={(saved) => {
            setStatutoryNode(null);
            if (saved) showSuccess('Statutory report forms updated.');
          }}
        />
      )}

      {deletingNode && (
        <DeleteNodeModal
          node={deletingNode}
          hasChildren={nodes.some((n) => n.parent_id === deletingNode.id)}
          impact={deleteImpact}
          impactLoading={impactLoading}
          onClose={closeDelete}
          onConfirm={confirmDelete}
          deleting={deleting}
          error={deleteError}
        />
      )}
    </>
  );

  if (hideNavigation) {
    return content;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      {content}
    </div>
  );
}
